import { cn } from '@/lib/utils';

export interface InputTabOption<T extends string> {
  value: T;
  label: string;
  /**
   * If true, render the tab in a "Coming soon" state — visible but
   * unclickable. Used by the AppConfig feature-flag gate so the user can
   * see what's planned without being able to submit through the disabled
   * input type.
   */
  comingSoon?: boolean;
}

export interface InputTabsProps<T extends string> {
  value: T;
  onChange: (next: T) => void;
  options: InputTabOption<T>[];
  disabled?: boolean;
  className?: string;
}

export function InputTabs<T extends string>({
  value,
  onChange,
  options,
  disabled,
  className,
}: InputTabsProps<T>) {
  return (
    <div role="tablist" className={cn('flex gap-1 border-b border-border', className)}>
      {options.map((opt) => {
        const active = opt.value === value;
        const isDisabled = disabled || opt.comingSoon;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            aria-disabled={isDisabled}
            disabled={isDisabled}
            onClick={() => !opt.comingSoon && onChange(opt.value)}
            title={opt.comingSoon ? 'Coming soon' : undefined}
            className={cn(
              '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors',
              active
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
              opt.comingSoon
                ? 'cursor-not-allowed text-muted-foreground/60 hover:text-muted-foreground/60'
                : 'disabled:opacity-50',
            )}
          >
            {opt.label}
            {opt.comingSoon && <span className="ml-1 text-xs">(soon)</span>}
          </button>
        );
      })}
    </div>
  );
}
